import { engineComponent, StringUtils, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { AnimationElement } from "../Components/Elements/Graphics/AnimationElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { StageController } from "../Components/StageController";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { Tweens } from "../Components/Effects/Tweens";

@engineComponent(ComponentTypes.DisplayObject)
export class TrailsParticleElement extends BaseElement {
    //  holds particles in pool and reused them
    private _pool: Array<AnimationElement>;

    // timer for add particles
    private _timer: FrameTimerEventEmitter;

    private _target: BaseElement;

    private _animations: Array<string>;
    private _count: number;
    private _time: number;
    private _width: number;
    private _height: number;
    private _targetX: number;
    private _targetY: number;
    private _maxAnimDuration: number;
    private _animRotation: number;
    private _tintColors: Array<string>;
    private _trailScaleX: Array<number>;

    private _stopping: boolean;
    private _stop: boolean;

    private _created: boolean;

    constructor() {
        super();

        this._pool = [];

        this._timer = CreateFrameTimer("trailsParticleTimer");
        this._timer.on(FrameTimerEvents.Complete, this.addParticles.bind(this));

        this._animations = [];
        this._count = 30;
        this._time = 10;
        this._width = 0;
        this._height = 0;
        this._targetX = 0;
        this._targetY = 0;
        this._maxAnimDuration = 1;
        this._animRotation = 0;
        this._tintColors = [];
        this._trailScaleX = [];

        this._created = false;
        this._stopping = false;
        this._stop = true;
    }

    //******************************************************************************************************************
    //  INITIALIZATION
    //******************************************************************************************************************

    createByNode(node: Element): void {
        if (!this._created) {
            super.createByNode(node);

            if (node.hasAttribute(TrailsParticleElementAttributes.ANIMATIONS)) {
                this._animations = node.getAttribute(TrailsParticleElementAttributes.ANIMATIONS).split(";");
            }

            if (node.hasAttribute(TrailsParticleElementAttributes.TIME)) {
                this._time = parseInt(node.getAttribute(TrailsParticleElementAttributes.TIME));
            }

            if (node.hasAttribute(TrailsParticleElementAttributes.COUNT)) {
                this._count = parseInt(node.getAttribute(TrailsParticleElementAttributes.COUNT));
            }

            if (node.hasAttribute(TrailsParticleElementAttributes.WIDTH)) {
                this._width = parseInt(node.getAttribute(TrailsParticleElementAttributes.WIDTH));
            }

            if (node.hasAttribute(TrailsParticleElementAttributes.HEIGHT)) {
                this._height = parseInt(node.getAttribute(TrailsParticleElementAttributes.HEIGHT));
            }

            if (node.hasAttribute(TrailsParticleElementAttributes.TARGET_X)) {
                this._targetX = parseInt(node.getAttribute(TrailsParticleElementAttributes.TARGET_X));
            }

            if (node.hasAttribute(TrailsParticleElementAttributes.TARGET_Y)) {
                this._targetY = parseInt(node.getAttribute(TrailsParticleElementAttributes.TARGET_Y));
            }

            if (node.hasAttribute(TrailsParticleElementAttributes.MAX_ANIM_DURATION)) {
                this._maxAnimDuration = parseInt(node.getAttribute(TrailsParticleElementAttributes.MAX_ANIM_DURATION));
            }

            if (node.hasAttribute(TrailsParticleElementAttributes.ANIM_ROTATION)) {
                this._animRotation = parseInt(node.getAttribute(TrailsParticleElementAttributes.ANIM_ROTATION));
            }

            if (node.hasAttribute(TrailsParticleElementAttributes.TRAIL_SCALE_X)) {
                this._trailScaleX = ArrayUtils.numberArray(
                    node.getAttribute(TrailsParticleElementAttributes.TRAIL_SCALE_X),
                );
            }

            this.createTargetElement();
            this.createParticles();
            this._created = true;
        }
    }

    updateByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(TrailsParticleElementAttributes.DEBUG) &&
                node.getAttribute(TrailsParticleElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        super.updateByNode(node);

        if (node.hasAttribute(TrailsParticleElementAttributes.TINT_COLORS)) {
            this._tintColors = node.getAttribute(TrailsParticleElementAttributes.TINT_COLORS).split(";");
        }

        if (
            node.hasAttribute(TrailsParticleElementAttributes.START) &&
            StringUtils.parseBoolean(node.getAttribute(TrailsParticleElementAttributes.START))
        ) {
            this.start();
        }

        if (
            node.hasAttribute(TrailsParticleElementAttributes.STOPPING) &&
            StringUtils.parseBoolean(node.getAttribute(TrailsParticleElementAttributes.STOPPING))
        ) {
            this.stopping();
        }

        if (
            node.hasAttribute(TrailsParticleElementAttributes.STOP) &&
            StringUtils.parseBoolean(node.getAttribute(TrailsParticleElementAttributes.STOP))
        ) {
            this.stop();
        }
    }

    //******************************************************************************************************************
    //  CREATING PARTICLES
    //******************************************************************************************************************

    private createTargetElement(): void {
        let resource: BaseElement;
        let node: Element = document.createElement("Target");
        resource = new BaseElement();
        resource.create(node);
        resource.x = this._targetX;
        resource.y = this._targetY;

        this._target = resource;
        this.addChild(resource);
    }

    public createParticles(): void {
        if (this._animations.length > 0) {
            let count = this.calculateElements();

            for (let i: number = 0; i < count; i++) {
                let particle: AnimationElement = this.createParticle();

                this._pool.push(particle);
            }
        }
    }

    private createParticle(): AnimationElement {
        let particle: AnimationElement;
        particle = this.createParticleResource();

        this.setupParticle(particle);

        this.addChild(particle);
        return particle;
    }

    private createParticleResource(): AnimationElement {
        let resource: AnimationElement;
        let resourceId = this.getRandomResource();
        let node: Element = document.createElement("Animation");
        node.setAttribute("Style2", resourceId);
        resource = new AnimationElement();
        resource.create(node);

        let localBounds = resource.getLocalBounds();
        resource.pivot.set(localBounds.width / 2, localBounds.height);
        // resource.rotation = this.degrees2radians(this._animRotation);

        return resource;
    }

    //******************************************************************************************************************
    //  SETUP
    //******************************************************************************************************************

    private setupParticle(particle: AnimationElement) {
        particle.visible = false;
        particle.usedInPool = false;
    }

    private setupStartPosition(particle: AnimationElement): void {
        particle.x = Math.random() * this._width;
        particle.y = Math.random() * this._height;
    }

    private setupSkew(particle: AnimationElement): void {
        let particleX = particle.x + this.x;
        let particleY = particle.y + this.y;

        let a = particleX - this._target.x;
        let b = Math.abs(particleY - this._target.y);

        let angleRadians = Math.atan2(a, b);
        particle.skew.x = angleRadians;

        this.setupScale(particle, a, b);
    }

    private setupScale(particle: AnimationElement, a: number, b: number): void {
        let distance = Math.sqrt(a * a + b * b);
        let localBounds = particle.getLocalBounds();
        let scaleY = distance / localBounds.height;

        if (this._trailScaleX.length == 2) {
            particle.scale.x = this.getRandom(this._trailScaleX[0], this._trailScaleX[1]);
        }

        particle.scale.y = scaleY;
    }

    private setupTint(particle: AnimationElement): void {
        if (this._tintColors.length > 0) {
            let color = this._tintColors[Math.floor(Math.random() * this._tintColors.length)];
            particle.tint = parseInt(color, 16);
        }
    }

    //******************************************************************************************************************
    //  GETTING PARTICLES
    //******************************************************************************************************************

    private addParticles(): void {
        if (!this._stop) {
            let tabActive = true;
            if (document.visibilityState != null && document.visibilityState === "hidden") {
                tabActive = false;
            }

            if (tabActive) {
                this.getFromPool();
            }

            if (!this._stopping) {
                this._timer.start(this._time);
            }
        }
    }

    private getFreeParticleIndex(): number {
        for (let i: number = 0; i < this._pool.length; i++) {
            if (!this._pool[i].usedInPool) {
                return i;
            }
        }

        return -1;
    }

    private getFromPool(): void {
        for (let i: number = 0; i < this._count; i++) {
            let particle: AnimationElement;
            let index: number = this.getFreeParticleIndex();

            // to keep pool ordered by usedInPool check if first element is usedInPool
            // if yes, create new and push to pool
            // if not, shift element, set usedInPool and push to pool
            if (index == -1) {
                particle = this.createParticle();

                this._pool.push(particle);
            } else {
                particle = this._pool[index];
            }

            if (particle != null) {
                particle.usedInPool = true;
                particle.visible = true;

                this.setupTint(particle);
                this.setupStartPosition(particle);
                this.setupSkew(particle);
                this.addAlphaBehaviour(particle);

                this.startAnimation(particle);
            }
        }
    }

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    private startAnimation(particle: AnimationElement): void {
        particle.playMe();
        particle.on(AnimationElement.AnimationComplete, this.onAnimationComplete.bind(this, particle));
    }

    private onAnimationComplete(particle: AnimationElement): void {
        particle.visible = false;
        particle.usedInPool = false;
    }

    //******************************************************************************************************************
    //  Tween
    //******************************************************************************************************************

    private addAlphaBehaviour(particle: AnimationElement): void {
        let alphas: Array<number> = [0, 0.6, 0.6, 0];
        let life = 180;

        Tweens.alphaTween(particle, {
            alphas: alphas,
            tweenDuration: life,
            paused: false,
        });
    }

    //******************************************************************************************************************
    //  CONTROL PARTICLES
    //******************************************************************************************************************

    start(): void {
        this.visible = true;

        if (this._pool.length > 0) {
            this._timer.stop();
            this._stopping = false;
            this._stop = false;

            this.addParticles();
        }
    }

    stopping(): void {
        this._stopping = true;
    }

    stop(): void {
        this._stopping = false;
        this._stop = true;
        this._timer.stop();
        this.visible = false;
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    calculateElements(): number {
        let elementsPerSecond: number = (StageController.FPS / this._time) * this._count;
        let elementsWithLife: number = (this._maxAnimDuration / StageController.FPS) * elementsPerSecond;
        return elementsWithLife;
    }

    private getRandomResource(): string {
        return this._animations[Math.floor(Math.random() * this._animations.length)];
    }

    private getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    //******************************************************************************************************************
    //  CLONE
    //******************************************************************************************************************

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return TrailsParticleElement
     */
    clone(): TrailsParticleElement {
        let element: TrailsParticleElement = new TrailsParticleElement();
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element._animations = this._animations;
        element._count = this._count;
        element._time = this._time;
        element._width = this._width;
        element._height = this._height;
        element._targetX = this._targetX;
        element._targetY = this._targetY;
        element._trailScaleX = this._trailScaleX;
        element._maxAnimDuration = this._maxAnimDuration;

        let node: Element = document.createElement(this.name);
        element.create(node);
        element._markAsCloned(); // must be called last before return!
        return element;
    }
}

export const TrailsParticleElementAttributes = {
    DEBUG: "Debug",

    ANIMATIONS: "Animations",
    BLEND_MODE: "BlendMode",
    COUNT: "Count",
    TIME: "Time",
    WIDTH: "Width",
    HEIGHT: "Height",
    TARGET_X: "TargetX",
    TARGET_Y: "TargetY",
    TINT_COLORS: "TintColors",
    START: "Start",
    STOPPING: "Stopping",
    STOP: "Stop",
    MAX_ANIM_DURATION: "MaxAnimDuration",
    ANIM_ROTATION: "AnimRotation",
    TRAIL_SCALE_X: "TrailScaleX",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/TrailsParticleElement.ts
 **/